import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Button,
  Table,
  Badge,
  Spinner,
  Alert,
  Form,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaClock,
  FaInfoCircle,
  FaArrowLeft,
} from "react-icons/fa";

import {
  getJobRuns,
  getJobRunById,
  getJobRunErrors,
  getJobRunEvents,
  getJobRunsByStatus,
} from "../services/APIClient";

// Constants
const CONSTANTS = {
  DEFAULT_LIMIT: 50,
  MAX_DETAILS_HEIGHT: "300px",
  MAX_TABLE_HEIGHT: "500px",
  STATUS_FILTERS: [
    { value: "all", label: "All Statuses" },
    { value: "completed", label: "Completed" },
    { value: "completed_with_limit", label: "With Limit" },
    { value: "failed", label: "Failed" },
    { value: "running", label: "Running" },
  ],
  STATUS_CONFIG: {
    completed: { bg: "success", icon: FaCheckCircle, text: "Completed" },
    completed_with_limit: {
      bg: "warning",
      icon: FaExclamationTriangle,
      text: "Completed (Limit Reached)",
    },
    failed: { bg: "danger", icon: FaTimes, text: "Failed" },
    running: { bg: "info", icon: FaClock, text: "Running" },
  },
};

function JobHistoryModal({ show, onHide }) {
  const [jobRuns, setJobRuns] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (show) {
      loadJobRuns();
      // Reset details when modal opens
      setSelectedJob(null);
      setJobDetails(null);
    }
  }, [show]);

  useEffect(() => {
    if (show && !selectedJob) {
      loadJobRuns();
    }
  }, [statusFilter]);

  const loadJobRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (statusFilter === "all") {
        data = await getJobRuns({ filter: { limit: CONSTANTS.DEFAULT_LIMIT } });
      } else {
        data = await getJobRunsByStatus(statusFilter, CONSTANTS.DEFAULT_LIMIT);
      }
      setJobRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load job runs:", err);
      setError("Failed to load job runs. Please try again.");
      setJobRuns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (job) => {
    setSelectedJob(job);
    setDetailsLoading(true);
    setError(null);
    
    try {
      const [details, errorsResponse, events] = await Promise.all([
        getJobRunById(job.id),
        getJobRunErrors(job.id).catch((err) => {
          console.error("Error fetching job errors:", err);
          return { errors: [] };
        }),
        getJobRunEvents(job.id).catch((err) => {
          console.error("Error fetching job events:", err);
          return [];
        }),
      ]);

      setJobDetails({
        ...details,
        errors: errorsResponse?.errors || [],
        events: Array.isArray(events) ? events : [],
      });
    } catch (err) {
      console.error("Failed to load job details:", err);
      setError("Failed to load job details. Please try again.");
      setJobDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedJob(null);
    setJobDetails(null);
    setError(null);
  };

  const handleModalClose = () => {
    handleBack();
    setStatusFilter("all");
    setError(null);
    onHide();
  };

  const getStatusBadge = (status) => {
    const config = CONSTANTS.STATUS_CONFIG[status] || {
      bg: "secondary",
      icon: FaInfoCircle,
      text: status || "Unknown",
    };
    const Icon = config.icon;

    return (
      <Badge bg={config.bg}>
        <Icon style={{ marginRight: "0.3rem" }} />
        {config.text}
      </Badge>
    );
  };

  const formatDuration = (start, end) => {
    if (!start) return "N/A";
    if (!end) return "In progress...";
    
    const diff = new Date(end) - new Date(start);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid date";
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleTimeString();
    } catch {
      return "Invalid time";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date/time";
    }
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {selectedJob
            ? `Job Run Details - ${selectedJob.id}`
            : "Job Run History"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <FaExclamationTriangle style={{ marginRight: "0.5rem" }} />
            {error}
          </Alert>
        )}

        {!selectedJob ? (
          // List View
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="mb-0">Threshold Check Executions</h5>
                <small className="text-muted">
                  History of automated and manual job runs
                </small>
              </div>
              <Form.Select
                style={{ width: "200px" }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
              >
                {CONSTANTS.STATUS_FILTERS.map((filter) => (
                  <option key={filter.value} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="text-muted mt-2">Loading job history...</p>
              </div>
            ) : jobRuns.length === 0 ? (
              <Alert variant="info">
                <FaInfoCircle style={{ marginRight: "0.5rem" }} />
                No job runs found.{" "}
                {statusFilter !== "all"
                  ? "Try changing the status filter."
                  : "Runs are created automatically daily or when manually triggered."}
              </Alert>
            ) : (
              <div style={{ maxHeight: CONSTANTS.MAX_TABLE_HEIGHT, overflowY: "auto" }}>
                <Table striped bordered hover responsive>
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Duration</th>
                      <th>Schools</th>
                      <th>Events</th>
                      <th>Notifications</th>
                      <th>Errors</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobRuns.map((job) => (
                      <tr key={job.id}>
                        <td>{getStatusBadge(job.status)}</td>
                        <td>
                          <div>{formatDate(job.startedAt)}</div>
                          <small className="text-muted">
                            {formatTime(job.startedAt)}
                          </small>
                        </td>
                        <td>{formatDuration(job.startedAt, job.completedAt)}</td>
                        <td>
                          <strong>{job.schoolsProcessed || 0}</strong>
                        </td>
                        <td>
                          <strong>{job.eventsCreated || 0}</strong>
                        </td>
                        <td>
                          <strong>{job.notificationsSent || 0}</strong>
                        </td>
                        <td>
                          {job.errorsCount > 0 ? (
                            <Badge bg="danger">{job.errorsCount}</Badge>
                          ) : (
                            <Badge bg="success">0</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleViewDetails(job)}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        ) : (
          // Details View
          <>
            {detailsLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="text-muted mt-2">Loading details...</p>
              </div>
            ) : jobDetails ? (
              <>
                <Row className="mb-4">
                  <Col md={6}>
                    <Card>
                      <Card.Body>
                        <h6 className="mb-3">Job Information</h6>
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr>
                              <td>
                                <strong>Status:</strong>
                              </td>
                              <td>{getStatusBadge(jobDetails.status)}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Job Name:</strong>
                              </td>
                              <td>{jobDetails.jobName || "N/A"}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Started:</strong>
                              </td>
                              <td>{formatDateTime(jobDetails.startedAt)}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Completed:</strong>
                              </td>
                              <td>{formatDateTime(jobDetails.completedAt)}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Duration:</strong>
                              </td>
                              <td>
                                {formatDuration(
                                  jobDetails.startedAt,
                                  jobDetails.completedAt
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col md={6}>
                    <Card>
                      <Card.Body>
                        <h6 className="mb-3">Execution Results</h6>
                        <table className="table table-sm mb-0">
                          <tbody>
                            <tr>
                              <td>
                                <strong>Schools Processed:</strong>
                              </td>
                              <td>{jobDetails.schoolsProcessed || 0}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Events Created:</strong>
                              </td>
                              <td>{jobDetails.eventsCreated || 0}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Notifications Sent:</strong>
                              </td>
                              <td>{jobDetails.notificationsSent || 0}</td>
                            </tr>
                            <tr>
                              <td>
                                <strong>Errors Count:</strong>
                              </td>
                              <td>
                                {jobDetails.errorsCount > 0 ? (
                                  <Badge bg="danger">
                                    {jobDetails.errorsCount}
                                  </Badge>
                                ) : (
                                  <Badge bg="success">0</Badge>
                                )}
                              </td>
                            </tr>
                            {jobDetails.summary?.emailLimitReached && (
                              <tr>
                                <td colSpan={2}>
                                  <Badge bg="warning">
                                    <FaExclamationTriangle
                                      style={{ marginRight: "0.3rem" }}
                                    />
                                    Email Limit Reached
                                  </Badge>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Errors Section */}
                {jobDetails.errors && jobDetails.errors.length > 0 && (
                  <Card className="mb-3">
                    <Card.Header className="bg-danger text-white">
                      <FaExclamationTriangle
                        style={{ marginRight: "0.5rem" }}
                      />
                      Errors ({jobDetails.errors.length})
                    </Card.Header>
                    <Card.Body>
                      <div
                        style={{
                          maxHeight: CONSTANTS.MAX_DETAILS_HEIGHT,
                          overflowY: "auto",
                        }}
                      >
                        <ul className="mb-0">
                          {jobDetails.errors.map((error, index) => (
                            <li key={index} className="text-danger mb-2">
                              {typeof error === "string"
                                ? error
                                : error.message || JSON.stringify(error)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {/* Events Section */}
                {jobDetails.events && jobDetails.events.length > 0 ? (
                  <Card>
                    <Card.Header>
                      <FaInfoCircle style={{ marginRight: "0.5rem" }} />
                      Events Created ({jobDetails.events.length})
                    </Card.Header>
                    <Card.Body>
                      <div
                        style={{
                          maxHeight: CONSTANTS.MAX_DETAILS_HEIGHT,
                          overflowY: "auto",
                        }}
                      >
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th>School</th>
                              <th>Event Type</th>
                              <th>Metric Value</th>
                              <th>Processed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {jobDetails.events.map((event) => (
                              <tr key={event.id}>
                                <td>{event.school?.name || event.schoolId}</td>
                                <td>{event.eventType || "N/A"}</td>
                                <td>
                                  <strong>
                                    {event.metricValue !== undefined &&
                                    event.metricValue !== null
                                      ? `${event.metricValue}%`
                                      : "N/A"}
                                  </strong>
                                </td>
                                <td>
                                  {event.processed ? (
                                    <Badge bg="success">Yes</Badge>
                                  ) : (
                                    <Badge bg="warning">No</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                ) : (
                  <Alert variant="info">
                    <FaInfoCircle style={{ marginRight: "0.5rem" }} />
                    No events were created during this job run.
                  </Alert>
                )}

                <div className="mt-3">
                  <Button variant="secondary" onClick={handleBack}>
                    <FaArrowLeft style={{ marginRight: "0.5rem" }} />
                    Back to List
                  </Button>
                </div>
              </>
            ) : (
              <Alert variant="warning">
                <FaExclamationTriangle style={{ marginRight: "0.5rem" }} />
                Failed to load job details. Please try again.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!selectedJob && (
          <Button variant="secondary" onClick={handleModalClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

JobHistoryModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default JobHistoryModal;